import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, Chip, TextField, InputAdornment, CircularProgress, IconButton, Tooltip, Snackbar, Alert, Button, Badge } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUsers } from '../api';
import { usePartnerColors, alpha } from '../components/partnerColors';   // [D-769] колір партнера - той самий, що в «🤝 Партнери»
import { PartnerTag } from '../components/partnerTag';   // [D-770] позначка - спільна з «Черга» / «Квитанції» / «Відео»
import axios from 'axios';

const statusColor = { active: '#69f0ae', trial: '#ffe57f', expired: '#f44336' };

export default function UsersPage() {
  const copy = (text) => { navigator.clipboard.writeText(text); };
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [wdDetail, setWdDetail] = useState(null); // [МЕДІА УНІКАЛІЗАТОР] оверлей деталей заявки
  const [rates, setRates] = useState({ eur_uah_rate: '', eur_usd_rate: '', fx_auto_enabled: 'true', fx_source_pref: 'auto', fx_margin_percent: '0', fx_rates_updated_at: '', fx_rates_source: '', fx_hold: '', eur_uah_pay_rate: '', fx_pay_updated_at: '', fx_hold_pay: '' }); // [МЕДІА УНІКАЛІЗАТОР] курси виводу (авто/ручний)
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const isMobile = useMediaQuery('(max-width:900px)');
  const [limitDialog, setLimitDialog] = React.useState(null);
  const [limitVal, setLimitVal] = React.useState('');
  const [mpsDialog, setMpsDialog] = React.useState(null);
  const [mpsVal, setMpsVal] = React.useState('');
  const [nowTs, setNowTs] = React.useState(Date.now()); // D-247: живий зворотній відлік
  const [sortBy, setSortBy] = useState('expiry'); // сортування по закінченню
  // [МЕДІА УНІКАЛІЗАТОР] 2026-09-11: 🆕 нові юзери. Набір фіксуємо на ПЕРШОМУ
  // завантаженні — далі позначка «прочитано» його не гасить, тож рядки лишаються
  // помітними, доки сторінка відкрита.
  const [newIds, setNewIds] = useState(null);
  // [D-769] кольори беремо з того самого джерела, що й сторінка партнерів:
  // один партнер - один колір скрізь, інакше позначка нічого не підказує.
  const pIds = React.useMemo(() => [...new Set(users.map(u => u.cab_pid || u.partner_id).filter(Boolean))].map(id => ({ id })), [users]);
  const { color: pColor } = usePartnerColors(pIds);
  const pOf = (u) => u.cab_pid || u.partner_id || 0;

  const saveMps = async () => {
    if (!mpsDialog) return;
    await fetch('/api/users/'+mpsDialog.id+'/max-per-session?value='+mpsVal,
      { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    setMpsDialog(null);
    getUsers().then(r => setUsers(r.data));
  };
  const resetMps = async () => {
    if (!mpsDialog) return;
    await fetch('/api/users/'+mpsDialog.id+'/max-per-session?value=0',
      { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    setMpsDialog(null);
    getUsers().then(r => setUsers(r.data));
  };
  const tok = localStorage.getItem('dashboard_token');

  const saveLimit = async () => {
    if (!limitDialog) return;
    await fetch('/api/users/'+limitDialog.id+'/daily-limit?limit='+limitVal,
      { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    setLimitDialog(null);
    getUsers().then(r => setUsers(r.data));
  };
  const navigate = useNavigate();
  const location = useLocation();
  const filterParam = new URLSearchParams(location.search).get('filter');

  // [МЕДІА УНІКАЛІЗАТОР] заявки на вивід (для badge + сум у рядках + вкладки)
  const reloadWithdrawals = () => fetch('/api/withdrawals', { headers: { Authorization: 'Bearer ' + tok } })
    .then(r => (r.ok ? r.json() : [])).then(d => { if (Array.isArray(d)) setWithdrawals(d); }).catch(() => {});

  const reloadRates = () => fetch('/api/referral/settings', { headers: { Authorization: 'Bearer ' + tok } })
    .then(r => (r.ok ? r.json() : {})).then(d => setRates({
      eur_uah_rate: d.eur_uah_rate || '', eur_usd_rate: d.eur_usd_rate || '', fx_hold: d.fx_hold || '',
      eur_uah_pay_rate: d.eur_uah_pay_rate || '', fx_pay_updated_at: d.fx_pay_updated_at || '', fx_hold_pay: d.fx_hold_pay || '',   // [D-657] курс monobank для цін ₴   // [D-656] курс не вигадуємо
      fx_auto_enabled: d.fx_auto_enabled || 'true', fx_source_pref: d.fx_source_pref || 'auto',
      fx_margin_percent: d.fx_margin_percent || '0', fx_rates_updated_at: d.fx_rates_updated_at || '', fx_rates_source: d.fx_rates_source || '',
    })).catch(() => {});
  const saveRates = async (extra) => {
    // [D-656] курс пишемо лише в ручному режимі - інакше старе значення з екрана перетерло б щойно оновлений авто-курс
    const manual = (extra && extra.fx_auto_enabled ? extra.fx_auto_enabled : rates.fx_auto_enabled) !== 'true';
    const body = Object.assign(manual ? { eur_uah_rate: String(rates.eur_uah_rate), eur_usd_rate: String(rates.eur_usd_rate) } : {}, {
      fx_auto_enabled: String(rates.fx_auto_enabled), fx_source_pref: String(rates.fx_source_pref), fx_margin_percent: String(rates.fx_margin_percent) }, extra || {});
    try {
      const r = await fetch('/api/referral/settings', { method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setSnack({ open: true, msg: '❌ ' + ((j && j.detail) || 'Помилка'), severity: 'error' }); return; }
      setSnack({ open: true, msg: '✅ Збережено', severity: 'success' }); reloadRates();
    } catch {}
  };
  const toggleFxAuto = () => {
    const next = rates.fx_auto_enabled === 'true' ? 'false' : 'true';
    setRates({ ...rates, fx_auto_enabled: next });
    saveRates({ fx_auto_enabled: next });
  };
  const fxRefreshNow = async () => {
    try {
      const r = await fetch('/api/fx/refresh', { method: 'POST', headers: { Authorization: 'Bearer ' + tok } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setSnack({ open: true, msg: '❌ ' + ((j && j.detail) || 'Не вдалось оновити курс'), severity: 'error' }); return; }
      setSnack({ open: true, msg: '✅ Курс оновлено: 1€=' + j.eur_uah_rate + '₴ (' + j.source + ')', severity: 'success' }); reloadRates();
    } catch {}
  };

  useEffect(() => {
    getUsers().then(r => {
      setUsers(r.data); setLoading(false);
      setNewIds(new Set((r.data || []).filter(u => u.is_new).map(u => u.id)));
      // «Прочитано» шлемо ПІСЛЯ відповіді, а не паралельно: інакше мітка могла б
      // лягти раніше, ніж сервер порахує is_new, і 🆕 не з'явилось би взагалі.
      fetch('/api/users/mark-new-seen', { method: 'POST', headers: { Authorization: 'Bearer ' + tok } })
        .catch(() => {});
    });
    reloadWithdrawals();
    reloadRates();
  }, []);
  // D-247: оновлювати зворотній відлік щохвилини
  useEffect(() => { const _t = setInterval(() => setNowTs(Date.now()), 60000); return () => clearInterval(_t); }, []);

  const pendingW = withdrawals.filter(w => w.status === 'pending');
  // мапа user_id → {amount,count} для активних заявок
  const pendingByUser = {};
  pendingW.forEach(w => {
    if (!pendingByUser[w.user_id]) pendingByUser[w.user_id] = { amount: 0, count: 0 };
    pendingByUser[w.user_id].amount += w.amount;
    pendingByUser[w.user_id].count += 1;
  });

  const wdPaid = async (w) => {
    if (!window.confirm('Підтвердити: ти ВЖЕ переказав ' + w.amount + '€ @' + w.username + ' (' + w.method + ')?\nБаланс юзера буде списано.')) return;
    try {
      const r = await fetch('/api/withdrawals/' + w.id + '/paid', { method: 'POST', headers: { Authorization: 'Bearer ' + tok } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setSnack({ open: true, msg: '❌ ' + ((j && j.detail) || 'Помилка'), severity: 'error' }); return; }
      setWdDetail(null); reloadWithdrawals(); getUsers().then(r => setUsers(r.data));
    } catch {}
  };
  const wdReject = async (w) => {
    const note = window.prompt('Причина відхилення (юзеру видно):', '');
    if (note === null) return;
    try { await fetch('/api/withdrawals/' + w.id + '/reject?note=' + encodeURIComponent(note), { method: 'POST', headers: { Authorization: 'Bearer ' + tok } }); setWdDetail(null); reloadWithdrawals(); } catch {}
  };
  const wdDelete = async (w) => {
    if (!window.confirm('Видалити запис заявки #' + w.id + ' (€' + w.amount + ' @' + w.username + ')?\nБаланс НЕ зміниться — прибирається лише запис (для очистки тестів).')) return;
    try { await fetch('/api/withdrawals/' + w.id, { method: 'DELETE', headers: { Authorization: 'Bearer ' + tok } }); setWdDetail(null); reloadWithdrawals(); } catch {}
  };
  // [МЕДІА УНІКАЛІЗАТОР] копіювання поля з фідбеком
  const copyVal = (val, label) => {
    try { navigator.clipboard.writeText(String(val)); setSnack({ open: true, msg: '📋 ' + (label || 'Скопійовано') + ': ' + String(val).slice(0, 30), severity: 'success' }); } catch {}
  };
  // рядок деталі заявки з кнопкою копіювання (для оверлею)
  const copyField = (label, value, opts) => {
    if (value === null || value === undefined || value === '') return null;
    const o = opts || {};
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.7, borderBottom: '1px solid #2a2a3e' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#888', fontSize: 10 }}>{label}</Typography>
          <Typography sx={{ color: o.color || '#fff', fontSize: o.big ? 16 : 13, fontWeight: o.big ? 'bold' : 'normal', wordBreak: 'break-all' }}>{value}</Typography>
        </Box>
        {o.copy !== false && (
          <Tooltip title="Копіювати">
            <IconButton size="small" onClick={() => copyVal(o.copyVal != null ? o.copyVal : value, label)} sx={{ color: 'var(--ac1, #7c4dff)', '&:hover': { color: 'var(--ac2, #a78bfa)' } }}><ContentCopyIcon sx={{ fontSize: 17 }}/></IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // [D-769] 🔴 Юзери партнерів ТЕПЕР ТУТ ЖЕ. Скасовує приховування з D-448
  // («щоб вона не змішувалась», OWNER 05.10) на вимогу OWNER 30.10: «мені
  // дуже не зручно перемикатися на партнера і там шукати юзерів, треба щоб
  // у моїх юзерів у звичайному списку був дубляж цих юзерів але позначений».
  // Не змішуємо наосліп: рядок фарбується кольором партнера й має підпис,
  // а фільтри «👤 Тільки мої» / «🤝 Юзери партнерів» дають обидві половини.
  const isPt = (u) => !!(u.partner_id || u.cab_of);
  const partnerCount = users.filter(isPt).length;
  const filtered = users.filter(u => {
    const _s = search.trim().replace(/^@/,'').toLowerCase();
    const matchSearch = (u.telegram_username || '').toLowerCase().includes(_s) || String(u.telegram_id || '').includes(_s);
    // 💎 PlayLanguage: filter by source; 🎬 Media: by media_joined; інакше by status
    const matchFilter = !filterParam
      || (filterParam === 'playlanguage' ? (u.source === 'playlanguage' || u.pl_user)
        : filterParam === 'avatar' ? !!u.mav_user
        : filterParam === 'media' ? !!u.media_joined
        : filterParam === 'founders' ? !!u.is_founder
        : filterParam === 'withdrawals' ? true
        // 💎 2026-08-14: «користування», а не «доступ» (як у PL)
        : filterParam === 'active_now' ? !!u.active_now
        : filterParam === 'quiet' ? !!u.quiet
        : filterParam === 'appclicks' ? ((u.app_opens || 0) + (u.app_previews || 0)) > 0   // [D-768]
        : filterParam === 'mine' ? !isPt(u)                 // [D-769] як було до 30.10
        : filterParam === 'partners' ? isPt(u)              // [D-769]
        : u.status === filterParam);
    return matchSearch && matchFilter;
  });

  const _effEnd = (u) => (u.unlimited || u.pl_forever) ? null : (u.subscription_end || u.trial_end || u.pl_access_until || null);
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy !== 'expiry') return 0;
    const eA = _effEnd(a), eB = _effEnd(b);
    if (!eA && !eB) return 0;
    if (!eA) return 1;
    if (!eB) return -1;
    return new Date(eA) - new Date(eB);
  });

  // 💎 PlayLanguage / 🎬 Media: quick filters (статус + джерело) + 💸 заявки на вивід
  const FILTERS = [
    { key: '', label: 'Всі' },
    // 💎 2026-08-14: КОРИСТУВАННЯ (зробив відео) — не плутати зі статусом доступу нижче
    { key: 'active_now', label: '🔥 Робили відео (7д)' },
    { key: 'quiet', label: '😴 Тихі' },
    { key: 'active', label: 'Active' },
    { key: 'trial', label: 'Trial' },
    { key: 'expired', label: 'Expired' },
    { key: 'playlanguage', label: '💎 PlayLanguage' },
    { key: 'avatar', label: '🎭 Avatar' },
    { key: 'media', label: '🎬 Media_Uniqueizer' },
    { key: 'appclicks', label: '👆 Ходили в APP' },   // [D-768]
    { key: 'mine', label: '👤 Тільки мої' },         // [D-769]
    { key: 'partners', label: '🤝 Юзери партнерів' },   // [D-769]
    { key: 'founders', label: '👑 Засновники' },
    { key: 'withdrawals', label: '💸 Заявки на вивід' },
  ];
  const showWithdrawals = filterParam === 'withdrawals';

  const getRemaining = (u) => {
    if (u.unlimited) return '∞';
    return Math.max(0, (u.daily_limit || 3) - (u.videos_today || 0)) + '/' + (u.daily_limit || 3);
  };

  // [МЕДІА УНІКАЛІЗАТОР] helper: днів до дати (ceil), null якщо дати немає
  const _dayNum = (endDate) => {
    if (!endDate) return null;
    return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  // D-247: зворотній відлік дд:гг:хв + колір (≤5д жовтий, ≤3д червоний) — як у Квитанціях
  const fmtCountdown = (endStr) => {
    if (!endStr) return { text: '—', color: '#aaa' };
    const ms = new Date(endStr).getTime() - nowTs;
    if (ms <= 0) return { text: 'Закінчився', color: '#f44336' };
    const totalMin = Math.floor(ms / 60000);
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;
    const parts = [];
    if (days > 0) parts.push(days + 'д');
    if (days > 0 || hours > 0) parts.push(hours + 'г');
    parts.push(mins + 'хв');
    const daysLeft = ms / 86400000;
    let color = '#4caf50';
    if (daysLeft <= 3) color = '#f44336';
    else if (daysLeft <= 5) color = '#ffe57f';
    return { text: parts.join(' '), color };
  };

  const getDaysLeft = (u) => {
    // [МЕДІА УНІКАЛІЗАТОР] Якщо у юзера є І Медіа, І PlayLanguage — показуємо
    // через "/" : "Медіа(відлік) / PL(днів)". Медіа = відлік дд:гг:хв з кольором, PL = бірюзовий.
    const _mediaEnd = u.subscription_end || u.trial_end;
    const _hasMedia = !!_mediaEnd;
    const _hasPL = u.pl_forever || !!u.pl_access_until || u.source === 'playlanguage' || u.pl_user;
    if (_hasMedia && _hasPL) {
      const cd = fmtCountdown(_mediaEnd);
      let _pPart;
      if (u.pl_forever) _pPart = '♾️';
      else { const _pd = _dayNum(u.pl_access_until); _pPart = _pd === null ? '—' : (_pd <= 0 ? '✖' : _pd + 'д'); }
      return (
        <span style={{ fontWeight: 'bold' }} title="Медіа / PlayLanguage">
          <span style={{ color: cd.color }}>{cd.text}</span>
          <span style={{ color: '#666' }}> / </span>
          <span style={{ color: '#ffe57f' }}>{_pPart}</span>
        </span>
      );
    }
    // 💎 PlayLanguage (Phase F fix): PL-only users have no Media subscription_end/
    // trial_end. Fall back to their effective PL access (pl_access_until), and show
    // ♾️ for lifetime (pl_forever) — so the column is no longer blank for PL users.
    if (!u.subscription_end && !u.trial_end && u.pl_forever)
      return <span style={{color:'#5eead4', fontWeight:'bold'}}>♾️ Назавжди</span>;
    const endDate = u.subscription_end || u.trial_end || u.pl_access_until;
    if (!endDate) return '—';
    const cd = fmtCountdown(endDate);
    return <span style={{ color: cd.color, fontWeight: 'bold' }}>{cd.text}</span>;
  };

  const handleRemind = async (e, userId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('dashboard_token');
      await axios.post('/api/users/' + userId + '/remind', {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setSnack({ open: true, msg: '✅ Нагадування надіслано!', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, msg: '❌ Помилка: ' + (err.response?.data?.detail || 'невідома'), severity: 'error' });
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }}/></Box>;

  return (
    <Box>
      <Typography variant="h5" sx={{ color: '#fff', mb: 1, fontWeight: 'bold' }}>
        👥 Користувачі {filterParam ? '- ' + ((FILTERS.find(f => f.key === filterParam) || {}).label || filterParam) : ''}
      </Typography>
      <Typography variant="body2" sx={{ color: '#aaa', mb: 2, bgcolor:'rgba(26,26,46,0.7)', px:1.5, py:0.5, borderRadius:1, display:'inline-block' }}>Всього: {filtered.length}{partnerCount > 0 && !filterParam ? ` · з них партнерських: ${filtered.filter(isPt).length}` : ''}</Typography>
      {/* [D-768] у цьому фільтрі список навмисно ширший за звичайний */}
      {filterParam === 'appclicks' && (
        <Typography variant="body2" sx={{ color: '#7ffcd3', mb: 2, ml: 1, px: 1.5, py: 0.5, borderRadius: 1, display: 'inline-block', border: '1px solid rgba(0,229,160,.3)' }}>
          👆 Хто переходив у наші застосунки — разом з учнями партнерів і самими партнерами. Зведення по застосунках — на сторінці «📱 Наші APP».
        </Typography>
      )}
      {partnerCount > 0 && (
        <Typography variant="body2" onClick={() => navigate('/partners')}
          sx={{ color: '#7ffcd3', mb: 2, ml: 1, px: 1.5, py: 0.5, borderRadius: 1, display: 'inline-block',
                cursor: 'pointer', border: '1px solid rgba(0,229,160,.3)', '&:hover': { bgcolor: 'rgba(0,229,160,.08)' } }}>
          🤝 {partnerCount} — юзери партнерів: вони в цьому ж списку, рядок пофарбовано кольором партнера. Відкрити «Партнери» →
        </Typography>
      )}
      <Box sx={{ display:'flex', flexWrap:'wrap', gap:1, mb:2 }}>
        {FILTERS.map(f => {
          const active = (filterParam || '') === f.key;
          const isPl = f.key === 'playlanguage';
          const isWd = f.key === 'withdrawals';
          const chip = (
            <Chip key={f.key || 'all'} label={f.label} size="small" clickable
              onClick={() => navigate('/users' + (f.key ? '?filter=' + f.key : ''))}
              sx={{
                bgcolor: active ? (isWd ? '#ff9800' : isPl ? 'rgba(124,58,237,0.85)' : 'var(--ac1, #7c4dff)') : 'rgba(26,26,46,0.9)',
                color: active ? '#fff' : (isWd && pendingW.length ? '#ffb74d' : '#aaa'), fontWeight: (active || (isWd && pendingW.length)) ? 'bold' : 'normal',
                border: '1px solid ' + (isWd && pendingW.length ? 'rgba(255,152,0,0.7)' : isPl ? 'rgba(167,139,250,0.6)' : '#333'),
              }}/>
          );
          // 💸 badge з к-стю непрочитаних (активних) заявок
          return isWd
            ? <Badge key="wd-badge" badgeContent={pendingW.length} color="error"
                sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>{chip}</Badge>
            : chip;
        })}
      </Box>
      <TextField placeholder="Пошук..." value={search} onChange={e => setSearch(e.target.value)}
        size="small" sx={{ mb: 2, bgcolor: '#1a1a2e', borderRadius: 1, input: { color: '#fff' },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#aaa' }}/></InputAdornment> }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip label={sortBy === 'expiry' ? '📅 За закінченням ↑' : '🕐 За реєстрацією'}
          size="small" clickable onClick={() => setSortBy(s => s === 'expiry' ? 'created' : 'expiry')}
          sx={{ bgcolor: sortBy === 'expiry' ? 'rgba(var(--ac1rgb, 124, 77, 255),0.3)' : 'rgba(26,26,46,0.9)', color: '#fff', border: '1px solid var(--ac1, #7c4dff)' }}
        />
        <Typography variant="caption" sx={{ color: '#666' }}>
          {sorted.length} юзерів
        </Typography>
      </Box>
      {/* [МЕДІА УНІКАЛІЗАТОР] 💸 вкладка заявок на вивід (перенесено зі Статистики) */}
      {showWithdrawals && (
        <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid ' + (pendingW.length ? '#ff9800' : '#333'), p: 2 }}>
          <Typography sx={{ color: '#ffb74d', mb: 1, fontWeight: 'bold' }}>💸 Заявки на вивід ({pendingW.length} в очікуванні)</Typography>
          {/* [МЕДІА УНІКАЛІЗАТОР] курс виводу: авто (open.er-api→PrivatBank) або ручний */}
          {(() => { const auto = rates.fx_auto_enabled === 'true'; return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 1.5, bgcolor: 'rgba(255,152,0,0.06)', p: 1, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#aaa', fontSize: 12, fontWeight: 'bold' }}>💱 Курс виводу:</Typography>
              <Button size="small" variant={auto ? 'contained' : 'outlined'} onClick={toggleFxAuto}
                sx={{ bgcolor: auto ? '#4caf50' : 'transparent', borderColor: '#4caf50', color: auto ? '#06210a' : '#4caf50', fontSize: 11, minWidth: 0, py: 0.2 }}>
                {auto ? '🔄 Авто' : '✋ Ручний'}
              </Button>
              {auto && (
                <>
                  <select value={rates.fx_source_pref} onChange={e => { const v = e.target.value; setRates({ ...rates, fx_source_pref: v }); saveRates({ fx_source_pref: v }); }}
                    style={{ background: '#1a1a2e', color: '#fff', border: '1px solid #444', borderRadius: 4, fontSize: 12, padding: '2px 4px' }}>
                    <option value="auto">авто (er-api→Privat)</option>
                    <option value="openerapi">open.er-api</option>
                    <option value="privatbank">PrivatBank</option>
                  </select>
                  <Button size="small" variant="outlined" onClick={fxRefreshNow} sx={{ borderColor: '#2196f3', color: '#2196f3', fontSize: 11, minWidth: 0, py: 0.2 }}>↻ Оновити зараз</Button>
                </>
              )}
              {rates.fx_rates_updated_at && <Typography sx={{ color: '#666', fontSize: 10 }}>📡 {rates.fx_rates_source} · {rates.fx_rates_updated_at}</Typography>}
              {rates.fx_hold && <Typography sx={{ color: '#ff9800', fontSize: 10.5 }}>⚠️ Курс тримається (D-656): {rates.fx_hold}</Typography>}
              {rates.eur_uah_pay_rate && <Typography sx={{ color: '#90caf9', fontSize: 10.5 }}>💳 Ціни пакетів у ₴ - за курсом monobank: 1€ = {rates.eur_uah_pay_rate}₴ (так і візьме банк) · {rates.fx_pay_updated_at}</Typography>}
              {rates.fx_hold_pay && <Typography sx={{ color: '#ff9800', fontSize: 10.5 }}>⚠️ Курс monobank тримається: {rates.fx_hold_pay}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <span style={{ color: '#aaa', fontSize: 12 }}>1€ =</span>
                <TextField size="small" type="number" InputProps={{ readOnly: auto }} value={rates.eur_uah_rate} onChange={e => setRates({ ...rates, eur_uah_rate: e.target.value })}
                  sx={{ width: 100, input: { color: '#fff', WebkitTextFillColor: '#fff', py: 0.4, fontSize: 14 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }}/>
                <span style={{ color: '#ffb74d', fontSize: 12 }}>₴</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <span style={{ color: '#aaa', fontSize: 12 }}>1€ =</span>
                <TextField size="small" type="number" InputProps={{ readOnly: auto }} value={rates.eur_usd_rate} onChange={e => setRates({ ...rates, eur_usd_rate: e.target.value })}
                  sx={{ width: 100, input: { color: '#fff', WebkitTextFillColor: '#fff', py: 0.4, fontSize: 14 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }}/>
                <span style={{ color: '#ffb74d', fontSize: 12 }}>$</span>
              </Box>
              {auto && (
                <Tooltip title="Запас % зменшує курс на твою користь — покриває банківський спред (0 = чистий ринковий)">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <span style={{ color: '#aaa', fontSize: 12 }}>Запас:</span>
                    <TextField size="small" type="number" value={rates.fx_margin_percent} onChange={e => setRates({ ...rates, fx_margin_percent: e.target.value })}
                      sx={{ width: 55, input: { color: '#fff', py: 0.4, fontSize: 13 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }}/>
                    <span style={{ color: '#ffb74d', fontSize: 12 }}>%</span>
                  </Box>
                </Tooltip>
              )}
              <Button size="small" variant="outlined" onClick={() => saveRates()} sx={{ borderColor: '#ff9800', color: '#ff9800', fontSize: 11, minWidth: 0, py: 0.2 }}>Зберегти</Button>
            </Box>
          </Box>
          ); })()}
          {withdrawals.length === 0 && <Typography sx={{ color: '#555', fontSize: 13 }}>— заявок ще немає (юзер подає через /withdraw у боті або «Вивести» у Хабі) —</Typography>}
          {/* [МЕДІА УНІКАЛІЗАТОР] компактний список — клік на рядок → оверлей з усіма даними + копіюванням */}
          {withdrawals.map(w => (
            <Box key={w.id} onClick={() => setWdDetail(w)}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 1.1, px: 1, borderTop: '1px solid #2a2a3e', borderRadius: 1, '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.1)' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                <Typography sx={{ color: w.status === 'pending' ? '#b794f6' : '#6b6b80', fontWeight: 'bold', fontSize: 15 }}>@{w.username}</Typography>
                <Typography sx={{ color: w.status === 'pending' ? '#ffd54f' : '#888', fontWeight: 'bold', fontSize: 14 }}>
                  €{w.amount}{w.currency !== 'EUR' && w.cur_amount ? ' → ' + w.cur_amount + ' ' + w.currency : ''}
                </Typography>
                <span style={{ fontSize: 13 }}>{w.src === 'media' ? '🎬' : '💎'}</span>
                {w.status !== 'pending' && <Chip label={w.status === 'completed' ? '✅ виплачено' : w.status === 'rejected' ? '✖ відхилено' : w.status} size="small"
                  sx={{ height: 18, fontSize: 10, bgcolor: w.status === 'completed' ? 'rgba(76,175,80,0.18)' : 'rgba(120,120,120,0.18)', color: w.status === 'completed' ? '#81c784' : '#999' }}/>}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                {w.status === 'pending' && (
                  <>
                    <Tooltip title="Виплачено"><IconButton size="small" onClick={() => wdPaid(w)} sx={{ color: '#4caf50', '&:hover': { bgcolor: 'rgba(76,175,80,0.15)' } }}><span style={{ fontSize: 16 }}>✅</span></IconButton></Tooltip>
                    <Tooltip title="Відхилити"><IconButton size="small" onClick={() => wdReject(w)} sx={{ color: '#f44', '&:hover': { bgcolor: 'rgba(244,67,54,0.15)' } }}><span style={{ fontSize: 15 }}>✖</span></IconButton></Tooltip>
                  </>
                )}
                <Tooltip title="Видалити запис"><IconButton size="small" onClick={() => wdDelete(w)} sx={{ color: '#666', '&:hover': { color: '#f44' } }}><span style={{ fontSize: 14 }}>🗑</span></IconButton></Tooltip>
              </Box>
            </Box>
          ))}
          <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1.5 }}>🖱️ Клік на заявку → деталі з копіюванням. «✅ Виплачено» списує баланс + DM юзеру. 🎬=Media, 💎=PL.</Typography>
        </Paper>
      )}
      {!showWithdrawals && (
      <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333' }}>
        <Table>
          <TableHead>
            <TableRow>
              {(isMobile ? ['Username','Статус','Залишилось','Днів до кінця'] : ['ID','Username','Статус','Залишилось','Днів до кінця','Всього відео','Реєстрація','🔔']).map(h => (
                <TableCell key={h} sx={{ color: 'var(--ac1, #7c4dff)', borderBottom: '1px solid #333', fontWeight: 'bold', py: 1 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(u => (
              <TableRow key={u.id} onClick={() => navigate('/users/' + u.id)} data-pt-of={pOf(u) || ''}
                sx={{ cursor: 'pointer',
                      /* [D-769] «новий» важливіший за колір партнера: червоне - те, що треба подивитись сьогодні */
                      bgcolor: (newIds && newIds.has(u.id)) ? 'rgba(255,45,45,0.13)' : (pOf(u) ? alpha(pColor(pOf(u)), 0.10) : 'transparent'),
                      '& td:first-of-type': pOf(u) ? { borderLeft: `4px solid ${pColor(pOf(u))}` } : {},
                      '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.1)' } }}>
                {!isMobile && <TableCell sx={{ color: '#aaa', borderBottom: '1px solid #222', py: 1 }}>{u.id}</TableCell>}
                <TableCell sx={{ color: '#fff', borderBottom: '1px solid #222', py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display:'flex', flexDirection:'column', gap:0.2 }}>
                      <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        @{u.telegram_username || '?'}
                        {newIds && newIds.has(u.id) && (
                          <Tooltip title="Новий юзер — ще не переглянутий">
                            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: '#ff8a80' }}>🆕</Box>
                          </Tooltip>
                        )}
                        {u.is_founder && <Tooltip title="Засновник"><span style={{fontSize:13}}>👑</span></Tooltip>}
                        {u.is_test && <Tooltip title="Тестовий акаунт: доступ не закривається за строком (тег «🧪 тест» у нотатках юзера)"><span style={{fontSize:13}}>🧪</span></Tooltip>}
                        <Tooltip title="Копіювати username"><IconButton size="small" onClick={e=>{e.stopPropagation();copy('@'+(u.telegram_username||''));}} sx={{ p:0.2, color:'#555','&:hover':{color:'var(--ac1, #7c4dff)'} }}><ContentCopyIcon sx={{ fontSize:12 }}/></IconButton></Tooltip>
                        {/* 💎 PlayLanguage (Phase F): real product logos instead of «💎 PL» text.
                            Media-acquired user who also uses PL → both logos; PL-only → PL logo. */}
                        {/* [МЕДІА УНІКАЛІЗАТОР] Media-лого по media_joined НЕЗАЛЕЖНО від PL;
                            PL-лого по PL. Чистий Media-юзер теж бачить лого (раніше блок гейтився за PL). */}
                        {(u.media_joined || u.pl_user || u.source === 'playlanguage' || u.mav_user) && (
                          <Box sx={{ display:'flex', alignItems:'center', gap:0.3 }}>
                            {u.media_joined && (
                              <Tooltip title="Media"><Box component="img" src="/icon-192.png" alt="Media"
                                sx={{ width:21, height:21, borderRadius:'5px', objectFit:'cover', border:'1px solid rgba(var(--ac1rgb, 124, 77, 255),0.5)' }}/></Tooltip>
                            )}
                            {(u.pl_user || u.source === 'playlanguage') && (
                              <Tooltip title="PlayLanguage"><Box component="img" src="/media/pl_logo/pl_logo.jpg" alt="PlayLanguage"
                                sx={{ width:21, height:21, borderRadius:'5px', objectFit:'cover', border:'1px solid rgba(167,139,250,0.6)' }}/></Tooltip>
                            )}
                            {u.mav_user && (
                              <Tooltip title="Avatar"><Box component="img" src="/media/avatar_logo/avatar_logo.png" alt="Avatar"
                                sx={{ width:21, height:21, borderRadius:'5px', objectFit:'cover', border:'1px solid rgba(236,72,153,0.6)' }}/></Tooltip>
                            )}
                          </Box>
                        )}
                        {/* [D-769] чий це юзер. Кабінет партнера показуємо інакше, ніж учня:
                            той самий акаунт буває і тим, і тим, і плутати їх не можна. */}
                        <PartnerTag who={pOf(u) ? { pid: pOf(u), name: u.cab_of || u.partner_name, cab: !!u.cab_of } : null} color={pColor}/>
                        {/* [D-768] переходи в наші застосунки видно одразу в списку; деталі - у картці юзера */}
                        {((u.app_opens || 0) + (u.app_previews || 0)) > 0 && (
                          <Tooltip title={'Переходив у наші застосунки: відкривав ' + (u.app_opens || 0) + ', дивився презентацію ' + (u.app_previews || 0) + (u.app_last ? ' · востаннє ' + new Date(u.app_last).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '')}>
                            <Chip label={'👆 ' + ((u.app_opens || 0) + (u.app_previews || 0))} size="small"
                              sx={{ height: 16, fontSize: 10, fontWeight: 'bold', bgcolor: 'rgba(0,229,160,0.18)', color: '#7ffcd3' }}/>
                          </Tooltip>
                        )}
                        <Tooltip title={u.can_publish ? 'Публікація: дозволено' : 'Публікація: заборонено'}>
                          <Chip label={u.can_publish ? '📢 Публікація' : '🚫 Без публікації'} size="small"
                            sx={{ height:16, bgcolor: u.can_publish ? 'rgba(76,175,80,0.22)' : 'rgba(120,120,120,0.18)',
                              color: u.can_publish ? '#81c784' : '#999', fontSize:9, fontWeight:'bold',
                              border: '1px solid ' + (u.can_publish ? 'rgba(76,175,80,0.5)' : 'rgba(120,120,120,0.4)'),
                              '& .MuiChip-label':{ px:0.6 } }}/>
                        </Tooltip>
                        {/* [МЕДІА УНІКАЛІЗАТОР] 💸 активна заявка на вивід — сума, клік→вкладка заявок */}
                        {pendingByUser[u.id] && (
                          <Tooltip title={'Активна заявка на вивід: €' + pendingByUser[u.id].amount.toFixed(2)}>
                            <Chip label={'💸 €' + pendingByUser[u.id].amount.toFixed(0)} size="small"
                              onClick={e => { e.stopPropagation(); navigate('/users?filter=withdrawals'); }}
                              sx={{ height:16, bgcolor:'rgba(255,152,0,0.22)', color:'#ffb74d', fontSize:9, fontWeight:'bold',
                                border:'1px solid rgba(255,152,0,0.6)', cursor:'pointer', '& .MuiChip-label':{ px:0.6 } }}/>
                          </Tooltip>
                        )}
                        {isMobile && <IconButton size="small" onClick={e => handleRemind(e, u.id)} sx={{ color: '#FFD700', p: 0.5 }}><NotificationsIcon sx={{ fontSize: 18 }}/></IconButton>}
                      </Box>
                      <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                        <Typography variant="caption" sx={{ color:'#555' }}>ID: {u.telegram_id}</Typography>
                        <Tooltip title="Копіювати ID"><IconButton size="small" onClick={e=>{e.stopPropagation();copy(String(u.telegram_id));}} sx={{ p:0.2, color:'#555','&:hover':{color:'var(--ac1, #7c4dff)'} }}><ContentCopyIcon sx={{ fontSize:12 }}/></IconButton></Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #222', py: 1 }}>
                  <Chip label={u.status} size="small" sx={{ bgcolor: statusColor[u.status] || '#666', color: '#1a1a2e', fontSize: 11, fontWeight:'bold' }}/>
                </TableCell>
                <TableCell sx={{ color: u.unlimited ? 'var(--ac1, #7c4dff)' : '#aaa', borderBottom: '1px solid #222', py: 1 }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                    {getRemaining(u)}
                    {u.max_per_session && <Typography variant="caption" sx={{ color:'#2196f3', fontSize:10, display:'block' }}>🎬{u.max_per_session}/раз</Typography>}
                    <Tooltip title="Змінити ліміт/день">
                      <IconButton size="small" onClick={e=>{e.stopPropagation();setLimitVal(String(u.daily_limit||3));setLimitDialog(u);}}
                        sx={{ p:0.2, color:'#555','&:hover':{color:'#ff9800'} }}>
                        <span style={{fontSize:11}}>✏️</span>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ліміт за раз">
                      <IconButton size="small" onClick={e=>{e.stopPropagation();setMpsVal(String(u.max_per_session||''));setMpsDialog(u);}}
                        sx={{ p:0.2, color:'#555','&:hover':{color:'#2196f3'} }}>
                        <span style={{fontSize:11}}>🎬</span>
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #222', py: 1, color: 'inherit' }}>{getDaysLeft(u)}</TableCell>
                {!isMobile && <TableCell sx={{ color: '#aaa', borderBottom: '1px solid #222', py: 1 }}>{u.total_videos || 0} <span style={{color:'#555',fontSize:11}}>({u.total_versions||0} верс.)</span></TableCell>}
                {!isMobile && <TableCell sx={{ color: '#aaa', borderBottom: '1px solid #222', py: 1 }}>{u.created_at ? u.created_at.substring(0,10) : '—'}</TableCell>}
                {!isMobile && <TableCell sx={{ borderBottom: '1px solid #222', py: 1 }}>
                  <Tooltip title="Надіслати нагадування">
                    <IconButton size="medium" onClick={e => handleRemind(e, u.id)}
                      sx={{ color: '#FFD700', '&:hover': { color: '#FFA500' }, p: 1.5 }}>
                      <NotificationsIcon fontSize="small"/>
                    </IconButton>
                  </Tooltip>
                </TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      )}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({...snack, open: false})}>
        <Alert severity={snack.severity} sx={{ bgcolor: snack.severity === 'success' ? 'rgba(76,175,80,0.9)' : 'rgba(244,67,54,0.9)', color: '#fff' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    {mpsDialog && (
      <Box sx={{ position:'fixed', top:0, left:0, right:0, bottom:0, bgcolor:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
        <Box sx={{ bgcolor:'#1a1a2e', border:'1px solid #2196f3', p:3, borderRadius:2, minWidth:280 }}>
          <Typography sx={{ color:'#fff', mb:1 }}>🎬 Ліміт за раз для <b>{mpsDialog.telegram_username || String(mpsDialog.telegram_id)}</b></Typography>
          <Typography variant="caption" sx={{ color:'#555', display:'block', mb:2 }}>Залиш порожнім — використовувати глобальний</Typography>
          <TextField size="small" type="number" value={mpsVal} onChange={e=>setMpsVal(e.target.value)}
            label="Версій за раз" inputProps={{ min:1, max:50 }}
            sx={{ mb:2, input:{color:'#fff'}, label:{color:'#aaa'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#2196f3'} }}/>
          <Box sx={{ display:'flex', gap:1 }}>
            <Button variant="contained" onClick={saveMps} sx={{ bgcolor:'#2196f3' }}>Зберегти</Button>
            <Button variant="outlined" onClick={resetMps} sx={{ borderColor:'#f44', color:'#f44' }}>Скинути</Button>
            <Button variant="outlined" onClick={()=>setMpsDialog(null)} sx={{ borderColor:'#555', color:'#aaa' }}>Скасувати</Button>
          </Box>
        </Box>
      </Box>
    )}
    {limitDialog && (
      <Box sx={{ position:'fixed', top:0, left:0, right:0, bottom:0, bgcolor:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
        <Box sx={{ bgcolor:'#1a1a2e', border:'1px solid #ff9800', p:3, borderRadius:2, minWidth:280 }}>
          <Typography sx={{ color:'#fff', mb:2 }}>✏️ Ліміт для <b>{limitDialog.telegram_username || String(limitDialog.telegram_id)}</b></Typography>
          <TextField size="small" type="number" value={limitVal} onChange={e=>setLimitVal(e.target.value)}
            label="Версій/день" inputProps={{ min:1, max:9999 }}
            sx={{ mb:2, input:{color:'#fff'}, label:{color:'#aaa'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#ff9800'} }}/>
          <Box sx={{ display:'flex', gap:1 }}>
            <Button variant="contained" onClick={saveLimit} sx={{ bgcolor:'#ff9800' }}>Зберегти</Button>
            <Button variant="outlined" onClick={()=>setLimitDialog(null)} sx={{ borderColor:'#555', color:'#aaa' }}>Скасувати</Button>
          </Box>
        </Box>
      </Box>
    )}
    {/* [МЕДІА УНІКАЛІЗАТОР] оверлей деталей заявки на вивід — усі дані з копіюванням */}
    {wdDetail && (
      <Box sx={{ position:'fixed', top:0, left:0, right:0, bottom:0, bgcolor:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000, p:2 }}
        onClick={() => setWdDetail(null)}>
        <Box sx={{ bgcolor:'#16162a', border:'1px solid '+(wdDetail.status==='pending'?'#ff9800':'#444'), borderRadius:2, p:2.5, width:'100%', maxWidth:430, maxHeight:'88vh', overflowY:'auto' }}
          onClick={e=>e.stopPropagation()}>
          <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1.5 }}>
            <Typography sx={{ color:'#ffb74d', fontWeight:'bold', fontSize:16 }}>💸 Заявка #{wdDetail.id} {wdDetail.src==='media'?'🎬':'💎'}</Typography>
            <IconButton size="small" onClick={()=>setWdDetail(null)} sx={{ color:'#888' }}><span style={{fontSize:18}}>✕</span></IconButton>
          </Box>
          {copyField('Username', '@'+wdDetail.username, { color:'#b794f6', big:true, copyVal:'@'+wdDetail.username })}
          {copyField('Сума (EUR — списується з балансу)', '€'+wdDetail.amount, { color:'#ffd54f', big:true, copyVal:wdDetail.amount })}
          {wdDetail.currency!=='EUR' && wdDetail.cur_amount ? copyField('💱 Переказати у валюті', wdDetail.cur_amount+' '+wdDetail.currency+(wdDetail.rate?'  (курс '+wdDetail.rate+')':''), { color:'#ff9800', big:true, copyVal:wdDetail.cur_amount }) : null}
          {copyField('👤 ПІБ отримувача', wdDetail.name)}
          {copyField('🏦 Банк', wdDetail.bank)}
          {copyField('💳 Реквізити (картка / IBAN / гаманець)', wdDetail.requisites, { big:true })}
          {copyField('💬 Коментар', wdDetail.comment, { color:'#90caf9' })}
          {copyField('Метод', wdDetail.method, { copy:false })}
          {copyField('Статус', (wdDetail.status==='completed'?'✅ виплачено':wdDetail.status==='rejected'?'✖ відхилено':wdDetail.status)+(wdDetail.admin_note?'  ('+wdDetail.admin_note+')':''), { copy:false })}
          {copyField('Подано', wdDetail.requested_at, { copy:false })}
          {wdDetail.status==='pending' ? copyField('Баланс юзера', '€'+wdDetail.balance, { copy:false }) : null}
          <Box sx={{ display:'flex', gap:1, mt:2 }}>
            {wdDetail.status==='pending' && (
              <>
                <Button variant="contained" onClick={()=>wdPaid(wdDetail)} sx={{ bgcolor:'#4caf50', '&:hover':{bgcolor:'#43a047'}, flex:1, fontSize:13 }}>✅ Виплачено</Button>
                <Button variant="outlined" onClick={()=>wdReject(wdDetail)} sx={{ borderColor:'#f44', color:'#f44', fontSize:13 }}>✖ Відхилити</Button>
              </>
            )}
            <Button variant="outlined" onClick={()=>wdDelete(wdDetail)} sx={{ borderColor:'#666', color:'#888', minWidth:0 }}>🗑</Button>
          </Box>
          <Button fullWidth onClick={()=>{ const id=wdDetail.user_id; setWdDetail(null); if(id) navigate('/users/'+id); }} sx={{ color:'var(--ac1, #7c4dff)', mt:1, fontSize:12 }}>Відкрити профіль юзера →</Button>
        </Box>
      </Box>
    )}
    </Box>
  );
}
